import type { NexperienceOpeningHoursQuery } from '@data-access/graphql/graphql'
import { TZDate } from '@date-fns/tz'
import { getTodayInDefaultTimeZone, parseLocalDate } from '@utils/helpers'
import { formatIsoDate } from '@utils/utils-format'
import { addDays, differenceInCalendarDays, startOfWeek } from 'date-fns'
import { DEFAULT_TIME_ZONE } from '@/lib/time-zone'
import { serializeExperienceInterval } from './experience-date-range'
import type { Experience, ExperienceFilter } from './experience-state-types'

export const TICKET_DAYS_PREVIEW_COUNT = 5
export const TICKET_CALENDAR_HORIZON = 365
export type TicketCalendarPlan = {
  today: string
  from: string
  to: string
  offerStart: string | null
  offerEnd: string | null
}
export type PurchasableDay = {
  date: string
  weekday: number
  windows: { from: string; to: string }[]
}

type OpeningDays = NonNullable<NexperienceOpeningHoursQuery['nexperience']>['openingHours']

/** Berlin calendar dates are computed once per request, not in the browser's zone. */
export function getTicketCalendarPlan(
  experience: Partial<Pick<Experience, 'offerPeriodStart' | 'offerPeriodEnd'>>,
  today = getTodayInDefaultTimeZone()
): TicketCalendarPlan {
  const horizon = formatIsoDate(addDays(today, TICKET_CALENDAR_HORIZON))
  const from = formatIsoDate(startOfWeek(today, { weekStartsOn: 1 }))
  const offerStart = experience.offerPeriodStart
    ? formatIsoDate(parseLocalDate(experience.offerPeriodStart))
    : null
  const offerEnd = experience.offerPeriodEnd
    ? formatIsoDate(parseLocalDate(experience.offerPeriodEnd))
    : null
  const end = offerEnd && offerEnd < horizon ? offerEnd : horizon
  return {
    today: formatIsoDate(today),
    from,
    // Keep the transport range valid even for an expired offer. The projection
    // below excludes every day outside the actual offer period.
    to: end < from ? from : end,
    offerStart,
    offerEnd,
  }
}

export function getTicketRequestedRange(plan: TicketCalendarPlan, filter: ExperienceFilter) {
  const { from, to } = serializeExperienceInterval(filter)
  if (!from) return null
  const start = plan.offerStart && from < plan.offerStart ? plan.offerStart : from
  const requestedEnd = to ?? from
  const end = plan.offerEnd && requestedEnd > plan.offerEnd ? plan.offerEnd : requestedEnd
  if (start > end) return null
  return start < plan.from || end > plan.to ? { from: start, to: end } : null
}

export function projectPurchasableDays(
  days: OpeningDays,
  today: string,
  offerEnd?: string | null,
  offerStart?: string | null
): PurchasableDay[] {
  return days
    .filter(
      day =>
        !day.closed &&
        day.date >= today &&
        (!offerStart || day.date >= offerStart) &&
        (!offerEnd || day.date <= offerEnd)
    )
    .map(day => ({ date: day.date, weekday: day.weekday, windows: day.windows }))
}

export function ticketPreviewDays(days: PurchasableDay[], filter: ExperienceFilter) {
  const { from, to } = serializeExperienceInterval(filter)
  return days
    .filter(day => (!from || day.date >= from) && (!to || day.date <= to))
    .slice(0, TICKET_DAYS_PREVIEW_COUNT)
}

export function ticketCalendarHorizon(plan: TicketCalendarPlan) {
  const today = new TZDate(plan.today, DEFAULT_TIME_ZONE)
  const lastDay = new TZDate(plan.to < plan.today ? plan.today : plan.to, DEFAULT_TIME_ZONE)
  return { lastDay, horizonDays: Math.max(0, differenceInCalendarDays(lastDay, today)) }
}
